import useQueryAvailabilityCalendar from 'app/hooks/useQueryAvailabilityCalendar'
import dayjs from 'lib/dayjs'
import { useParams } from 'next/navigation'
import { EBISU_HOTEL_CONFIG, JA_LANGUAGE } from 'utils/constants'

const useAvailableCalendar = (
  currentActiveStartDate: Date,
  rooms: {
    adults: number
    children: number
  }[],
  hotelId?: number,
  showResale?: boolean,
) => {
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const getEbisuDisabledDates = (): Date[] => {
    const isEbisuHotel = hotelId === EBISU_HOTEL_CONFIG.id
    const isJapanese = locale === JA_LANGUAGE

    if (!isEbisuHotel || !isJapanese) {
      return []
    }

    // Disable all dates up to and including March 16, 2026
    // First selectable date is March 17, 2026
    const cutoffDate = dayjs(EBISU_HOTEL_CONFIG.cutoffDate)
    const today = dayjs().startOf('day')
    const dates: Date[] = []

    // Generate dates from today up to (but not including) March 17, 2026
    let current = today
    while (current.isBefore(cutoffDate, 'day')) {
      dates.push(current.toDate())
      current = current.add(1, 'day')
    }

    return dates
  }

  const previousMonth = dayjs(currentActiveStartDate)
    .add(-1, 'month')
    .format('YYYY/MM')

  const currentMonth = dayjs(currentActiveStartDate).format('YYYY/MM')
  const nextMonth = dayjs(currentActiveStartDate)
    .add(1, 'month')
    .format('YYYY/MM')
  const next2Month = dayjs(currentActiveStartDate)
    .add(2, 'month')
    .format('YYYY/MM')

  const {
    data: previousMonthData,
    isLoading: isLoadingPreviousMonth,
    isError: isErrorPreviousMonth,
  } = useQueryAvailabilityCalendar({
    year_month: previousMonth,
    hotelId,
    nights: 1,
    rooms,
    showResale,
  })

  const {
    data: currentMonthData,
    isLoading: isLoadingCurrentMonth,
    isError: isErrorCurrentMonth,
  } = useQueryAvailabilityCalendar({
    year_month: currentMonth,
    hotelId,
    nights: 1,
    rooms,
    showResale,
  })

  const {
    data: nextMonthData,
    isLoading: isLoadingNextMonth,
    isError: isErrorNextMonth,
  } = useQueryAvailabilityCalendar({
    year_month: nextMonth,
    hotelId,
    nights: 1,
    rooms,
    showResale,
  })

  const {
    data: nextTwoMonthData,
    isLoading: isLoadingNextTwoMonth,
    isError: isErrorNextTwoMonth,
  } = useQueryAvailabilityCalendar({
    year_month: next2Month,
    hotelId,
    nights: 1,
    rooms,
    showResale,
  })

  const ebisuDisabledDates = getEbisuDisabledDates()

  // API disabled dates from Tripla/PocketRD (no inventory)
  const disabledDates =
    previousMonthData && currentMonthData && nextMonthData && nextTwoMonthData
      ? [
          ...(previousMonthData?.disabledDates || []),
          ...(currentMonthData?.disabledDates || []),
          ...(nextMonthData?.disabledDates || []),
          ...(nextTwoMonthData?.disabledDates || []),
        ].map((date: string | Date) =>
          date instanceof Date ? date : new Date(date),
        )
      : undefined

  return {
    isLoading:
      isLoadingCurrentMonth ||
      isLoadingNextMonth ||
      isLoadingNextTwoMonth ||
      isLoadingPreviousMonth,
    isError:
      isErrorCurrentMonth ||
      isErrorNextMonth ||
      isErrorNextTwoMonth ||
      isErrorPreviousMonth,
    disabledDates,
    ebisuDisabledDates,
  }
}

export default useAvailableCalendar
